import { Component, EventEmitter, Input, Output, Optional } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import {
  CisProSearchService,
  CisProSearchRequest,
  CisProSearchResult
} from '../../services/cispro-search.service';
import { ConfirmDialogComponent, ConfirmDialogData } from '../../shared/confirm-dialog/confirm-dialog.component';

export interface CisProSearchCriteria {
  materialName: string;
  productDesc: string;
  labelName: string;
  manufacturer: string;
  serviceCompany: string;
  rigName: string;
  location: string;
  barcodeId: string;
  receivedFrom: string | null;
  receivedTo: string | null;
  notes: string;
  showOwnedLocationsOnly: boolean;
}

@Component({
  selector: 'app-cispro-search',
  imports: [CommonModule, FormsModule, MatDialogModule],
  templateUrl: './cispro-search.component.html',
  styleUrls: ['./cispro-search.component.css']
})
export class CisProSearchComponent {
  readonly defaultPageSize = 50;
  readonly pageSizeOptions = [25, 50, 100];
  hasSearched = false;

  @Input() isOpen = false;
  @Output() onClose = new EventEmitter<void>();
  @Output() onSelect = new EventEmitter<CisProSearchResult>();

  get isVisible(): boolean {
    return this.isOpen || !!this.dialogRef;
  }

  searchCriteria: CisProSearchCriteria = {
    materialName: '',
    productDesc: '',
    labelName: '',
    manufacturer: '',
    serviceCompany: '',
    rigName: '',
    location: '',
    barcodeId: '',
    receivedFrom: null,
    receivedTo: null,
    notes: '',
    showOwnedLocationsOnly: true
  };

  searchResults: CisProSearchResult[] = [];
  originalSearchResults: CisProSearchResult[] = [];
  selectedResult: CisProSearchResult | null = null;
  isSearching = false;
  searchError: string | null = null;
  totalCount = 0;
  currentPage = 1;
  pageSize = this.defaultPageSize;

  constructor(
    private cisProSearchService: CisProSearchService,
    private dialog: MatDialog,
    @Optional() private dialogRef: MatDialogRef<CisProSearchComponent> | null = null
  ) {}

  search(): void {
    this.currentPage = 1;
    this.performSearch();
  }

  goToPreviousPage(): void {
    if (this.currentPage <= 1 || this.isSearching) {
      return;
    }

    this.currentPage--;
    this.updateVisibleResults();
  }

  goToNextPage(): void {
    if (this.currentPage >= this.totalPages || this.isSearching) {
      return;
    }

    this.currentPage++;
    this.updateVisibleResults();
  }

  onPageSizeChange(event: Event): void {
    const nextPageSize = Number((event.target as HTMLSelectElement).value);
    if (!Number.isFinite(nextPageSize) || nextPageSize === this.pageSize) {
      return;
    }

    this.pageSize = nextPageSize;

    if (this.hasSearched) {
      this.currentPage = 1;
      this.updateVisibleResults();
    }
  }

  get totalPages(): number {
    return this.totalCount > 0 ? Math.ceil(this.totalCount / this.pageSize) : 1;
  }

  get firstVisibleResultNumber(): number {
    return this.totalCount === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
  }

  get lastVisibleResultNumber(): number {
    return this.totalCount === 0
      ? 0
      : Math.min(this.currentPage * this.pageSize, this.totalCount);
  }

  private updateVisibleResults(): void {
    const firstResultIndex = (this.currentPage - 1) * this.pageSize;
    this.searchResults = this.originalSearchResults.slice(firstResultIndex, firstResultIndex + this.pageSize);
  }

  private performSearch(): void {
    this.isSearching = true;
    this.hasSearched = false;
    this.searchError = null;
    this.selectedResult = null;

    const request: CisProSearchRequest = {
      materialName: this.searchCriteria.materialName.trim(),
      productDescription: this.searchCriteria.productDesc.trim(),
      labelName: this.searchCriteria.labelName.trim(),
      manufacturer: this.searchCriteria.manufacturer.trim(),
      serviceCompany: this.searchCriteria.serviceCompany.trim(),
      rigName: this.searchCriteria.rigName.trim(),
      location: this.searchCriteria.location.trim(),
      barcodeId: this.searchCriteria.barcodeId.trim(),
      notes: this.searchCriteria.notes.trim(),
      receivedFrom: this.parseReceivedFrom(),
      receivedTo: this.parseReceivedTo(),
      showOwnedLocationsOnly: this.searchCriteria.showOwnedLocationsOnly
    };
    
    this.cisProSearchService.searchContainers(request).subscribe({
      next: (results) => {
        this.originalSearchResults = results;
        this.totalCount = results.length;
        this.updateVisibleResults();
        this.isSearching = false;
        this.hasSearched = true;
      },
      error: (error) => {
        console.error('Search failed:', error);
        this.searchError = error?.error?.detail || error?.error?.details || 'An error occurred during the search.';
        this.originalSearchResults = [];
        this.searchResults = [];
        this.totalCount = 0;
        this.isSearching = false;
        this.hasSearched = true;
        this.showSearchError();
      }
    });
  }

  private showSearchError(): void {
    if (!this.searchError) {
      return;
    }

    this.dialog.open<ConfirmDialogComponent, ConfirmDialogData>(ConfirmDialogComponent, {
      width: '380px',
      data: { title: 'Search Error', message: this.searchError, confirmLabel: 'OK', cancelLabel: '' }
    });
  }

  selectResult(result: CisProSearchResult): void {
    this.selectedResult = result;
  }

  selectResultAndConfirm(result: CisProSearchResult): void {
    this.selectedResult = result;
    this.confirmSelection();
  }

  confirmSelection(): void {
    if (this.selectedResult) {
      this.onSelect.emit(this.selectedResult);
      this.dialogRef?.close(this.selectedResult);
    }
  }


  closeModal(): void {
    this.onClose.emit();
    this.dialogRef?.close();
    this.resetSearch();
  }

  resetSearch(): void {
    this.searchCriteria = {
      materialName: '',
      productDesc: '',
      labelName: '',
      manufacturer: '',
      serviceCompany: '',
      rigName: '',
      location: '',
      barcodeId: '',
      receivedFrom: null,
      receivedTo: null,
      notes: '',
      showOwnedLocationsOnly: true
    };
    this.originalSearchResults = [];
    this.searchResults = [];
    this.selectedResult = null;
    this.searchError = null;
    this.hasSearched = false;
    this.totalCount = 0;
    this.currentPage = 1;
    this.pageSize = this.defaultPageSize;
  }

  private parseReceivedFrom(): Date | null {
    if (!this.searchCriteria.receivedFrom) {
      return null;
    }

    return new Date(`${this.searchCriteria.receivedFrom}`);
  }

  private parseReceivedTo(): Date | null {
    if (!this.searchCriteria.receivedTo) {
      return null;
    }

    return new Date(`${this.searchCriteria.receivedTo}`);
  }
}
